import { useQuery } from '@tanstack/vue-query'
import { ActionRequestResponseDto, ActionRequestType } from '@torticity/types'
import orderBy from 'lodash/orderBy.js'
import type { MaybeRef } from 'vue'
import { computed, unref } from 'vue'

export interface UseActionRequestsQueryOptions {
  filterById?: string | null
  filterByStatus?: string[]
  filterBySignatureStatus?: string[]
  enabled?: MaybeRef<boolean | undefined>
  documentGroupsOnly?: boolean
  filterByIsPriority?: MaybeRef<boolean | undefined>
  householdId?: MaybeRef<string | null | undefined>
}

export function useActionRequestsQuery(options: UseActionRequestsQueryOptions = {}) {
  const {
    filterById,
    filterByStatus,
    filterBySignatureStatus,
    enabled,
    documentGroupsOnly = false,
    filterByIsPriority,
    householdId,
  } = options
  const { $http, $flagsmith } = useNuxtApp()

  const route = useRoute()
  const isPublic = route.path.includes('public')

  async function getActionRequests(): Promise<ActionRequestResponseDto[]> {
    let actionRequests: ActionRequestResponseDto[]

    if (filterById) {
      actionRequests = await $http.$get(`/v1/action-requests/${filterById}`)
    } else {
      // Build query parameters for backend filtering
      const queryParams = new URLSearchParams()

      if (filterByStatus) {
        filterByStatus.forEach(status => queryParams.append('status', status))
      }

      if (filterBySignatureStatus) {
        filterBySignatureStatus.forEach(signatureStatus =>
          queryParams.append('signatureStatus', signatureStatus),
        )
      }

      const isPriority = unref(filterByIsPriority)
      if (isPriority !== undefined) {
        queryParams.append('isPriority', String(isPriority))
      }

      const selectedHouseholdId = unref(householdId)
      if (selectedHouseholdId) {
        queryParams.append('householdId', selectedHouseholdId)
      }

      const queryString = queryParams.toString()
      // Use document-groups endpoint when documentGroupsOnly is true
      const baseUrl = documentGroupsOnly
        ? '/v1/action-requests/document-groups'
        : '/v1/action-requests'
      const url = queryString ? `${baseUrl}?${queryString}` : baseUrl

      actionRequests = await $http.$get(url)

      // Only check feature flags for lists, not single actions
      // Single actions are checked in the RequestRouter component
      if (!$flagsmith.hasFeature('casewise_appointments')) {
        actionRequests = actionRequests.filter(
          action => action.type !== ActionRequestType.APPOINTMENT,
        )
      }
    }

    return actionRequests.length
      ? orderBy(actionRequests, ar => Number(ar.id), 'desc')
      : actionRequests
  }

  return useQuery({
    queryFn: getActionRequests,
    queryKey: computed(() => [
      'action-requests',
      filterById,
      filterByStatus,
      filterBySignatureStatus,
      documentGroupsOnly,
      unref(filterByIsPriority),
      unref(householdId),
    ]),
    enabled: computed(() => {
      const enabledVal = unref(enabled)
      return enabledVal !== undefined ? enabledVal : !isPublic
    }),
  })
}
